import { MonoTypeOperatorFunction, Observable, map, of, tap, timer } from 'rxjs';
import { catchError, filter, first, retry, switchMap, take } from 'rxjs/operators';

function polling<T>(
  stopCondition: (data: T) => boolean,
  repeatAttempts = 30,
  repeatAttemptInterval = 6000,
  errorCallbackFn?: (data: T) => unknown,
): MonoTypeOperatorFunction<T> {
  if (repeatAttempts < 1) {
    repeatAttempts = 1;
  }
  if (repeatAttemptInterval < 0) {
    repeatAttemptInterval = 0;
  }
  let repeatAttemptsLast = repeatAttempts;
  return (source$: Observable<T>): Observable<T> => {
    return timer(0, repeatAttemptInterval).pipe(
      tap(() => repeatAttemptsLast--),
      take(repeatAttempts),
      switchMap(() => source$.pipe(retry({ count: repeatAttempts, delay: repeatAttemptInterval }))),
      filter((data) => stopCondition(data) || repeatAttemptsLast === 0),
      map((data) => {
        if (!stopCondition(data) && repeatAttemptsLast === 0) {
          if (errorCallbackFn) {
            throw errorCallbackFn(data);
          }
          throw new Error(`No result received during ${repeatAttempts} attempts`);
        }
        return data;
      }),
      first(),
    );
  };
}

/**
 * Observable will be polled with an interval until the condition is met.
 * @param stopCondition - a condition which will be checked each iteration, until the condition is met.
 * @param repeatAttempts - default attempts number is 30. If an attempt fails it will retry the same amount of times. Minimum attempts number is 1.
 * @param repeatAttemptInterval - default repeat interval is 6 seconds.
 * @param noResultCallback - optional callback when all attempts fail
 */
export function pollUntil<T>(
  stopCondition: (data: T) => boolean,
  repeatAttempts = 30,
  repeatAttemptInterval = 6000,
  errorCallbackFn?: (data: T) => unknown,
): MonoTypeOperatorFunction<T> {
  return (source$: Observable<T>): Observable<T> =>
    source$.pipe(
      catchError((err: unknown) => {
        return of(err as T);
      }),
      polling(stopCondition, repeatAttempts, repeatAttemptInterval, errorCallbackFn),
    );
}
